/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import DisposableList from '../../lang/DisposableList';
import IDisposable from '../../lang/IDisposable';
import {AudioCodec} from '../decode/bitstream/codec/AudioCodec';
import {VideoCodec} from '../decode/bitstream/codec/VideoCodec';
import {IEncodedStreamSink} from './EncodedStreamSink';
import {StreamTransformContextTrack} from '../context/StreamTransformContext';
import SurrogateFrameDataManager, {SurrogateFrameType, SkipFrame} from './SurrogateFrameDataManager';
import {TransformMessageDataType} from './worker/TransformStreamPipelineWorkerMessage';
import Chunk from './Chunk';

export type EncodedMediaTransformManagerContext = {
  videoTrack: StreamTransformContextTrack;
  audioTrack: StreamTransformContextTrack;
  encodedVideoStreamSink: IEncodedStreamSink<RTCEncodedVideoFrame>;
  encodedAudioStreamSink: IEncodedStreamSink<RTCEncodedAudioFrame>;
};

export default class EncodedMediaTransformManager implements IDisposable {
  private readonly _disposables: DisposableList = new DisposableList();

  constructor(context: EncodedMediaTransformManagerContext) {
    this.initialize(context);
  }

  public dispose(): void {
    this._disposables.dispose();
  }

  private initialize(context: EncodedMediaTransformManagerContext): void {
    if (context.videoTrack.mediaStreamTrack) {
      context.videoTrack.transformFunctionNames.push('protected-video');

      this._disposables.add(context.videoTrack.transformMessageRouter!.registerRoute(TransformMessageDataType.EncodedVideoChunk, (chunk: RTCEncodedVideoFrame) => this.encodedVideoStreamCallback(context.videoTrack, context.encodedVideoStreamSink as IEncodedStreamSink<RTCEncodedVideoFrame>, chunk)));
    }

    if (context.audioTrack.mediaStreamTrack) {
      context.audioTrack.transformFunctionNames.push('protected-audio');

      this._disposables.add(context.audioTrack.transformMessageRouter!.registerRoute(TransformMessageDataType.EncodedAudioChunk, (chunk: RTCEncodedAudioFrame) => this.encodedAudioStreamCallback(context.audioTrack, context.encodedAudioStreamSink as IEncodedStreamSink<RTCEncodedAudioFrame>, chunk)));
    }
  }

  private async encodedAudioStreamCallback(track: StreamTransformContextTrack, encodedAudioStreamSink: IEncodedStreamSink<RTCEncodedAudioFrame>, chunk: RTCEncodedAudioFrame): Promise<Chunk> {
    if (!await encodedAudioStreamSink(track.mediaStreamTrack!, chunk)) {
      chunk.data = SurrogateFrameDataManager.getInvalidSurrogateAudioData();
    } else {
      chunk.data = SurrogateFrameDataManager.getSurrogateAudioSilentPerCodec(track.codec as AudioCodec) || chunk.data;
    }

    return chunk as unknown as Chunk;
  }

  private async encodedVideoStreamCallback(track: StreamTransformContextTrack, encodedVideoStreamSink: IEncodedStreamSink<RTCEncodedVideoFrame>, chunk: RTCEncodedVideoFrame): Promise<Chunk | undefined> {
    if (!await encodedVideoStreamSink(track.mediaStreamTrack!, chunk)) {
      let surrogate: SurrogateFrameType;

      if (chunk.type === 'key') {
        surrogate = SurrogateFrameDataManager.getInvalidSurrogateVideoIFrame(track.codec as VideoCodec);
      } else {
        surrogate = SurrogateFrameDataManager.getInvalidSurrogateVideoPFrame();
      }

      if (surrogate === SkipFrame) {
        return;
      }

      chunk.data = surrogate || chunk.data;
    } else {
      if (chunk.type === 'key') {
        chunk.data = SurrogateFrameDataManager.getSurrogateVideoIFramePerCodec(track.codec as VideoCodec) || chunk.data;
      } else {
        chunk.data = SurrogateFrameDataManager.getSurrogateVideoPFramePerCodec(track.codec as VideoCodec) || chunk.data;
      }
    }

    return chunk;
  }
}